import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "./api";
import { useAuth } from "./AuthContext";

// Default sample = the user's exact "L98.9 + Dermapen + Dull skin" reproduction case.
const SAMPLE = {
  procedure_done: "Dermapen",
  treatment_code: "17999",
  icd_code: "L98.9 Disorder of the skin and subcutaneous tissue, unspecified",
  chief_complaint:
    "Patient came for Dermapen treatment of dull skin and uneven skin tone for beauty purpose.",
  hpi: "Severity: dull skin | Context: dermapen | Location: face",
  ros: "Constitutional (General): Normal healthy",
  examination: "Constitutional (General)",
  narrative_diagnosis: "Encounter for Dermapen for face",
  final_diagnosis: "Encounter for cosmetic surgery",
  treatment_plan: "Dermapen of face done by using SkinPen for 4 sessions.",
  procedure_parameters: "depth 1.5mm, 6 passes",
  informed_consent: "signed",
  patient_education: "given",
  skin_type: "3",
  hair_type: "",
  encounter_type: "No Bed + No emergency room",
  speciality: "Dermatology",
  age: "32",
  gender: "Female",
};

const PRESETS = [
  {
    label: "Dermapen + L98.9 + Dull skin (should be NO Irregularity)",
    data: SAMPLE,
  },
  {
    label: "Laser Hair Removal + Z41.1 + 17999 (should be NO Irregularity)",
    data: {
      ...SAMPLE,
      procedure_done: "Laser hair removal",
      treatment_code: "17999",
      icd_code: "Z41.1 Encounter for cosmetic surgery",
      chief_complaint:
        "Patient came for laser hair removal of unwanted normal hair for beauty purpose.",
      hpi: "Severity: thin to thick hair | Context: laser hair removal | Location: full body",
      narrative_diagnosis: "Encounter for laser hair removal for full body",
      treatment_plan: "Laser hair removal of full body done by using ALEX/NDYAG for 6 sessions.",
      speciality: "Dermatology",
      procedure_parameters: "ALEX 16J/cm2, 10ms, 18mm spot; NDYAG 50J/cm2, 30ms, 10mm spot",
    },
  },
  {
    label: "LHR + Emergency room encounter (should be Irregularity +ve)",
    data: {
      ...SAMPLE,
      procedure_done: "Laser hair removal",
      treatment_code: "17999",
      icd_code: "Z41.1 Encounter for cosmetic surgery",
      encounter_type: "No Bed + Emergency room",
      chief_complaint:
        "Patient came for laser hair removal of unwanted normal hair for beauty purpose.",
    },
  },
];

const FIELDS = [
  ["procedure_done", "Procedure / Treatment Done", "text"],
  ["treatment_code", "Treatment Code (CPT)", "text"],
  ["icd_code", "ICD Code", "text"],
  ["chief_complaint", "Chief Complaint", "area"],
  ["hpi", "History of Present Illness (HPI)", "area"],
  ["ros", "Review of Systems (ROS)", "area"],
  ["examination", "Examination", "area"],
  ["narrative_diagnosis", "Narrative Diagnosis", "text"],
  ["final_diagnosis", "Final Diagnosis", "text"],
  ["treatment_plan", "Treatment Plan", "area"],
  ["procedure_parameters", "Procedure Parameters", "area"],
  ["informed_consent", "Informed Consent", "text"],
  ["patient_education", "Patient Education", "text"],
  ["skin_type", "Skin Type", "text"],
  ["hair_type", "Hair Type", "text"],
  ["encounter_type", "Encounter Type", "text"],
  ["speciality", "Speciality", "text"],
  ["age", "Age", "text"],
  ["gender", "Gender", "text"],
];

export default function RuleTesterPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [claim, setClaim] = useState(SAMPLE);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const set = (k, v) => setClaim((c) => ({ ...c, [k]: v }));

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/rules/test-claim", claim);
      setResult(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const loadPreset = (preset) => {
    setClaim(preset.data);
    setResult(null);
    setError(null);
  };

  const v = result?.verdict?.verdict;
  const verdictColor =
    v === "NO Irregularity" ? "#10b981"
    : v === "Irregularity +ve" ? "#ef4444"
    : v === "CLARIFY" ? "#f59e0b"
    : "#64748b";

  return (
    <div data-testid="rule-tester-page" style={S.page}>
      <header style={S.header}>
        <div>
          <div style={S.brand}>Target Health · Rule Tester</div>
          <div style={S.subbrand}>
            Paste a claim → see which rules fire → see the verdict — without running Dok32.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={S.userPill}>{user?.email}</span>
          <button data-testid="tester-back-rules-btn" onClick={() => nav("/admin")} style={S.linkBtn}>
            Rules
          </button>
          {user?.role === "admin" && (
            <button data-testid="tester-users-btn" onClick={() => nav("/admin/users")} style={S.linkBtn}>
              Users
            </button>
          )}
          <button data-testid="tester-logout-btn" onClick={logout} style={S.linkBtn}>
            Sign out
          </button>
        </div>
      </header>

      <section style={S.presets}>
        <span style={S.presetLabel}>Quick presets:</span>
        {PRESETS.map((p, i) => (
          <button
            key={i}
            data-testid={`tester-preset-${i}`}
            onClick={() => loadPreset(p)}
            style={S.presetBtn}
          >
            {p.label}
          </button>
        ))}
      </section>

      <div style={S.grid}>
        <section style={S.form}>
          <div style={S.formHead}>Claim fields</div>
          {FIELDS.map(([key, label, kind]) => (
            <div key={key} style={S.field}>
              <label style={S.label}>{label}</label>
              {kind === "area" ? (
                <textarea
                  data-testid={`tester-field-${key}`}
                  value={claim[key] || ""}
                  onChange={(e) => set(key, e.target.value)}
                  rows={2}
                  style={S.textarea}
                />
              ) : (
                <input
                  data-testid={`tester-field-${key}`}
                  value={claim[key] || ""}
                  onChange={(e) => set(key, e.target.value)}
                  style={S.input}
                />
              )}
            </div>
          ))}
          <button
            data-testid="tester-run-btn"
            onClick={run}
            disabled={busy}
            style={{ ...S.primary, opacity: busy ? 0.6 : 1 }}
          >
            {busy ? "Running engine…" : "▶ Run engine on this claim"}
          </button>
        </section>

        <section style={S.results}>
          <div style={S.formHead}>Result</div>
          {error && <div style={S.error} data-testid="tester-error">{error}</div>}
          {!result && !error && (
            <div style={S.placeholder}>
              Click <b>Run engine</b> to see which rules fire and what verdict the AI returns.
            </div>
          )}
          {result && (
            <div data-testid="tester-result">
              <div style={{ ...S.verdictBox, borderColor: verdictColor }}>
                <div style={S.verdictLabel}>FINAL VERDICT</div>
                <div style={{ ...S.verdictText, color: verdictColor }}
                     data-testid="tester-verdict">
                  {result.verdict?.verdict || "—"}
                </div>
                {result.verdict?.irregularity_site && (
                  <div style={S.verdictSite}>
                    Site: <span style={{ color: "#e2e8f0" }}>{result.verdict.irregularity_site}</span>
                  </div>
                )}
              </div>

              <Section title="DETECT rule matched" testid="tester-detect">
                {result.detect_rule_matched ? (
                  <div style={S.pillBox}>
                    <code style={S.code}>{result.detect_rule_matched.id}</code>
                    {" → "}
                    <b style={{ color: "#fca5a5" }}>{result.detect_rule_matched.verdict}</b>
                    {result.detect_rule_matched.site && (
                      <span style={{ color: "#94a3b8" }}> · {result.detect_rule_matched.site}</span>
                    )}
                  </div>
                ) : (
                  <div style={S.muted}>None</div>
                )}
              </Section>

              <Section title={`ACCEPT_IF / REJECT_IF policies applied (${result.policies_applied?.length || 0})`}
                       testid="tester-policies">
                {result.policies_applied?.length ? (
                  result.policies_applied.map((p) => (
                    <div key={p.id} style={S.policyRow}>
                      <code style={S.code}>{p.id}</code>
                      {p.scope && <span style={S.scope}> scope: {p.scope}</span>}
                      <div style={S.policyText}>{p.statement}{p.statement?.length === 240 ? "…" : ""}</div>
                    </div>
                  ))
                ) : (
                  <div style={S.muted}>None</div>
                )}
              </Section>

              <Section title={`GOLD_MATCH rows applied (${result.gold_applied?.length || 0})`}
                       testid="tester-gold">
                {result.gold_applied?.length ? (
                  result.gold_applied.map((g) => (
                    <div key={g.id} style={S.policyRow}>
                      <code style={S.code}>{g.id}</code>
                      <span style={S.scope}> field: {g.field} ({g.match_mode}, {g.severity})</span>
                      <div style={S.policyText}>expected: {g.expected}</div>
                    </div>
                  ))
                ) : (
                  <div style={S.muted}>None</div>
                )}
              </Section>

              <Section title="Engine output (raw)" testid="tester-raw">
                <pre style={S.pre}>{JSON.stringify(result.verdict, null, 2)}</pre>
              </Section>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div style={S.section} data-testid={testid}>
      <div style={S.sectionTitle}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh", background: "#0b1220", color: "#e2e8f0",
    fontFamily: "ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif",
    padding: "20px 24px 48px",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  brand: { fontSize: 18, fontWeight: 700, color: "#f1f5f9" },
  subbrand: { fontSize: 12, color: "#64748b", marginTop: 2 },
  userPill: {
    background: "#0f172a", border: "1px solid #1f2a44", padding: "4px 10px",
    borderRadius: 20, fontSize: 12, color: "#94a3b8",
  },
  linkBtn: {
    background: "transparent", border: "1px solid #334155", color: "#cbd5e1",
    padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12,
  },
  presets: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, alignItems: "center" },
  presetLabel: { fontSize: 12, color: "#64748b", letterSpacing: 1, textTransform: "uppercase" },
  presetBtn: {
    background: "#0f172a", border: "1px solid #334155", color: "#cbd5e1",
    padding: "6px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12,
  },
  grid: { display: "grid", gridTemplateColumns: "minmax(360px, 1fr) minmax(380px, 1.1fr)", gap: 20 },
  form: {
    background: "#0f172a", border: "1px solid #1f2a44", borderRadius: 10,
    padding: 18, maxHeight: "calc(100vh - 200px)", overflowY: "auto",
  },
  results: {
    background: "#0f172a", border: "1px solid #1f2a44", borderRadius: 10,
    padding: 18, maxHeight: "calc(100vh - 200px)", overflowY: "auto",
  },
  formHead: {
    fontSize: 14, fontWeight: 700, color: "#f1f5f9", marginBottom: 14,
    paddingBottom: 8, borderBottom: "1px solid #1f2a44",
  },
  field: { marginBottom: 10 },
  label: { display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4, letterSpacing: 0.5 },
  input: {
    width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #334155",
    background: "#0b1220", color: "#e2e8f0", fontSize: 13, outline: "none",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #334155",
    background: "#0b1220", color: "#e2e8f0", fontSize: 13, outline: "none",
    boxSizing: "border-box", fontFamily: "inherit", resize: "vertical",
  },
  primary: {
    width: "100%", padding: "11px 16px", borderRadius: 8, border: "none",
    background: "#10b981", color: "white", fontWeight: 700, cursor: "pointer",
    marginTop: 8, fontSize: 14,
  },
  placeholder: { color: "#64748b", padding: 20, textAlign: "center", fontSize: 13 },
  error: {
    background: "#450a0a", border: "1px solid #7f1d1d", color: "#fecaca",
    padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13,
  },
  verdictBox: {
    border: "2px solid #334155", borderRadius: 10, padding: "14px 16px",
    marginBottom: 16, background: "#0b1220",
  },
  verdictLabel: { fontSize: 10, color: "#64748b", letterSpacing: 1, textTransform: "uppercase" },
  verdictText: { fontSize: 22, fontWeight: 800, marginTop: 4 },
  verdictSite: { fontSize: 12, color: "#64748b", marginTop: 6 },
  section: { marginBottom: 14 },
  sectionTitle: {
    fontSize: 11, color: "#64748b", letterSpacing: 1, textTransform: "uppercase",
    marginBottom: 6,
  },
  pillBox: {
    background: "#0b1220", padding: "8px 10px", borderRadius: 6,
    border: "1px solid #1f2a44", fontSize: 13,
  },
  policyRow: {
    background: "#0b1220", padding: "8px 10px", borderRadius: 6,
    border: "1px solid #1f2a44", fontSize: 12, marginBottom: 6,
  },
  policyText: { marginTop: 4, color: "#94a3b8", lineHeight: 1.45 },
  scope: { color: "#fbbf24", fontSize: 11, marginLeft: 8 },
  muted: { color: "#64748b", fontSize: 12, fontStyle: "italic" },
  code: {
    background: "#1f2a44", color: "#a7f3d0", padding: "2px 6px",
    borderRadius: 4, fontSize: 11, fontFamily: "monospace",
  },
  pre: {
    background: "#0b1220", color: "#cbd5e1", padding: 10, borderRadius: 6,
    border: "1px solid #1f2a44", fontSize: 11, overflow: "auto",
    maxHeight: 240, margin: 0,
  },
};
